import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';

import { useScreenShortcuts } from '../contexts/ShortcutsContext';
import { focusListSearch } from '../components/items/ListSearchBox';
import { itemHomeCollectionId } from '../utils/linkedRecords';
import { normalizeId } from '../utils/ids';

/**
 * THE ITEMS PAGE'S KEYBOARD MAP (pages/CollectionItemsPage; moved here whole
 * by KPR-1644 so the page could take the pane's Enter without growing):
 * ↑/↓ move · Enter edit · Esc close the pane · `[` `]` the pane's width ·
 * X select · N new · / search.
 *
 * NOTE for the next person: these are TWO registries, and a key added to one
 * and not the other is a shortcut that either works silently or is listed and
 * dead. `useScreenShortcuts` below is the ⋯ → Keyboard shortcuts sheet; the
 * `document.keydown` handler under it is what actually fires. They live side
 * by side so the two cannot drift.
 *
 * The handler is registered ONCE: every volatile fact it reads comes from a
 * ref, so there is never a window in which a live listener answers with a
 * stale feed.
 *
 *   feedRef       { items, selectedItemId }
 *   selectRef     { enabled, disabledFor(item) }   the bulk selection's rules
 *   selectionRef  the bulk selection (`toggle`, `clear`, `active`)
 *   paneStepRef   the pane width's step(±1) (hooks/usePaneWidth)
 *   paneRef       the detail pane's { editing, enter } (ItemDetailPane controlRef)
 *   stepSelection one item up or down (hooks/useListStep)
 *   selectItem    the page's selection setter
 *   isMobile      a phone: Enter opens the item page, there is no pane to edit in
 */
export default function useItemsListKeys({
	collectionId,
	searchId,
	mapOn,
	boardOn,
	selectionEnabled,
	isMobile,
	feedRef,
	selectRef,
	selectionRef,
	paneStepRef,
	paneRef,
	stepSelection,
	selectItem,
}) {
	const navigate = useNavigate();
	const mobileRef = useRef(isMobile);
	mobileRef.current = isMobile;

	useScreenShortcuts('Item list', [
		{ keys: ['up'], label: 'Move up the list' },
		{ keys: ['down'], label: 'Move down the list' },
		{ keys: ['enter'], label: 'Edit the selected item' },
		{ keys: ['esc'], label: 'Clear the selection, then close the pane' },
		{ keys: ['['], label: 'Narrow the pane' },
		{ keys: [']'], label: 'Widen the pane' },
		{ keys: ['N'], label: 'Add an item' },
		{ keys: ['slash'], label: 'Search the list' },
		// The list beside the map moves its own focus (ItemsMapList); the
		// focused item is ringed on the map, and Enter or Space opens it.
		...(mapOn ? [
			{ keys: ['Home'], label: 'First item beside the map' },
			{ keys: ['End'], label: 'Last item beside the map' },
			{ keys: ['Page Down'], label: 'Ten items down beside the map' },
			{ keys: ['Page Up'], label: 'Ten items up beside the map' },
		] : []),
		// A focused card on the board moves a column (ItemsBoard); so does
		// Move to… in its ⋯.
		...(boardOn ? [
			{ keys: ['shift', 'left'], label: 'Move the focused card one column left' },
			{ keys: ['shift', 'right'], label: 'Move the focused card one column right' },
		] : []),
		...(selectionEnabled ? [
			{ keys: ['X'], label: 'Select the item you are on' },
			{ keys: ['shift', 'up'], label: 'Extend the selection up' },
			{ keys: ['shift', 'down'], label: 'Extend the selection down' },
			{ keys: ['shift', 'click'], label: 'Select a range' },
		] : []),
	]);

	useEffect(() => {
		const handler = (event) => {
			const target = event.target;
			if (target instanceof Element) {
				if (target.closest('input, textarea, select, [contenteditable="true"], .ant-modal, .ant-popover, .ant-drawer, .ant-select')) return;
			}
			if (event.metaKey || event.ctrlKey || event.altKey) return;

			const { items: navigableNow, selectedItemId: cursorId } = feedRef.current;
			// The item form open in the pane owns Esc (its Cancel) and Enter.
			const paneEditing = Boolean(paneRef?.current?.editing);

			if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
				event.preventDefault();
				stepSelection(event.key === 'ArrowDown' ? 1 : -1, event.shiftKey);
			} else if ((event.key === '[' || event.key === ']') && feedRef.current.selectedItemId) {
				// The pane one width narrower / wider (KPR-1643).
				event.preventDefault();
				paneStepRef.current?.(event.key === ']' ? 1 : -1);
			} else if (selectRef.current.enabled && (event.key === 'x' || event.key === 'X')) {
				// The item the cursor is on joins or leaves the selection.
				event.preventDefault();
				const item = navigableNow.find(row => normalizeId(row?._id) === cursorId);
				if (item && !selectRef.current.disabledFor(item)) selectionRef.current.toggle(cursorId);
			} else if (event.key === 'Enter') {
				// Enter on a button or a link is that control's own: with an item
				// selected it opened the item instead, and the toolbar (Sort,
				// Display, + Filter) could not be opened from the keyboard.
				if (target instanceof Element && target.closest('button, a[href], summary')) return;
				if (cursorId) {
					event.preventDefault();
					if (paneEditing) return;
					if (!mobileRef.current) {
						// The pane says what Enter does to its item (KPR-1644): the
						// form in the pane, Open for a linked row, nothing for a
						// record this person may only read or that is locked.
						paneRef?.current?.enter?.();
						return;
					}
					// A phone: the item page. A linked row opens where it lives.
					const cursorItem = navigableNow.find(row => normalizeId(row?._id) === cursorId);
					navigate(`/collections/${itemHomeCollectionId(cursorItem, collectionId)}/items/${cursorId}`);
				}
			} else if (event.key === 'Escape') {
				if (paneEditing) return;
				// The selection goes first: it is the bigger, more surprising
				// piece of state, and closing the pane out from under someone
				// who meant "never mind, drop the selection" is the wrong order.
				if (selectRef.current.enabled && selectionRef.current.active) {
					event.preventDefault();
					selectionRef.current.clear();
				} else if (cursorId) {
					event.preventDefault();
					selectItem(null);
				}
			} else if (event.key === 'n' || event.key === 'N') {
				event.preventDefault();
				document.querySelector('[data-action="add-item"]')?.click();
			} else if (event.key === '/') {
				event.preventDefault();
				// The search box when the list has one (KPR-740), else the filter.
				if (!focusListSearch(searchId)) document.getElementById('items-filter-trigger')?.click();
			}
		};
		document.addEventListener('keydown', handler);
		return () => document.removeEventListener('keydown', handler);
	}, [selectItem, navigate, collectionId, stepSelection, searchId, feedRef, selectRef, selectionRef, paneStepRef, paneRef]);
}
